import { ActionButton, cn, Dialog, TextField } from "@eisox/design-system";
import { useAppForm } from "@eisox/form";
import { useMediaQuery } from "@eisox/tools";
import { z } from "@eisox/zod";

import { FieldContainer } from "~/components/field-container";
import { MacQrScanner } from "~/components/mac-qr-scanner";
import { formatMacInput, macInputMaxLength, type MacDeviceType } from "~/helpers/mac-input.helper";
import { m } from "~/paraglide/messages";

interface ReplaceMacDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  icon: React.ReactElement;
  currentMac: string;
  existingMacs: (string | undefined)[];
  type: MacDeviceType;
  validateMac: (mac: string) => boolean;
  onSubmit: (mac: string) => Promise<unknown>;
}

const replaceMacSchema = (
  validateMac: (mac: string) => boolean,
  existingMacs: (string | undefined)[],
  currentMac: string,
) =>
  z.object({ mac: z.string() }).superRefine((data, ctx) => {
    if (!data.mac) return;

    if (!validateMac(data.mac)) {
      ctx.addIssue({ path: ["mac"], code: "custom", message: m.mac_invalid() });
      return;
    }

    if (existingMacs.includes(data.mac)) {
      ctx.addIssue({ path: ["mac"], code: "custom", message: m.mac_already_exists() });
      return;
    }

    if (data.mac === currentMac) {
      ctx.addIssue({ path: ["mac"], code: "custom", message: m.mac_invalid() });
    }
  });

export const ReplaceMacDialog: React.FC<ReplaceMacDialogProps> = ({
  open,
  onOpenChange,
  title,
  icon,
  currentMac,
  existingMacs,
  type,
  validateMac,
  onSubmit,
}) => {
  const isMobile = useMediaQuery("(max-width: 768px)");

  const schema = replaceMacSchema(validateMac, existingMacs, currentMac);

  const form = useAppForm({
    defaultValues: { mac: "" },
    validators: { onChange: schema, onSubmit: schema },
    onSubmit: async ({ value, formApi }) => {
      await onSubmit(value.mac);
      formApi.reset();
      onOpenChange(false);
    },
  });

  const handleOpenChange = (next: boolean) => {
    if (!next) form.reset();
    onOpenChange(next);
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content title={title} icon={icon}>
        <form
          className="w-full"
          onSubmit={e => {
            e.preventDefault();
            void form.handleSubmit();
          }}
        >
          <form.AppForm>
            <div className="flex w-full flex-col gap-[20px]">
              <div
                className={cn(
                  "[&>p:first-child]:font-medium flex flex-col gap-[6px] [&>p]:text-base [&>p:last-child]:text-center",
                )}
              >
                <p>{m.replace()}</p>
                <p>{currentMac}</p>
              </div>
              <form.Field name="mac">
                {field => {
                  const error = (field.state.meta.errors as z.core.$ZodIssue[] | undefined)?.[0];

                  return (
                    <FieldContainer label={m.replace_by()} error={error ? { message: error.message } : undefined}>
                      <TextField.Root
                        autoFocus={!isMobile}
                        autoComplete="off"
                        maxLength={macInputMaxLength(type)}
                        value={field.state.value}
                        placeholder={currentMac}
                        onChange={e => field.handleChange(formatMacInput(e.target.value, type))}
                        onBlur={field.handleBlur}
                      />
                    </FieldContainer>
                  );
                }}
              </form.Field>
              {isMobile && (
                <form.Field name="mac">
                  {field => (
                    <MacQrScanner paused={!open} onScan={raw => field.handleChange(formatMacInput(raw, type))} />
                  )}
                </form.Field>
              )}
              <form.Subscribe selector={state => ({ isDirty: state.isDirty, isSubmitting: state.isSubmitting })}>
                {({ isDirty, isSubmitting }) => (
                  <Dialog.Footer>
                    <Dialog.Close asChild>
                      <ActionButton variant="cancel">{m.cancel()}</ActionButton>
                    </Dialog.Close>
                    <ActionButton type="submit" variant="primary" disabled={!isDirty || isSubmitting}>
                      {m.replace()}
                    </ActionButton>
                  </Dialog.Footer>
                )}
              </form.Subscribe>
            </div>
          </form.AppForm>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
};

ReplaceMacDialog.displayName = "ReplaceMacDialog";
